"use client";

import {
  ReactNode,
  useContext,
  useEffect,
  useState,
  useActionState,
  useTransition,
} from "react";
import { createContext } from "react";
import {
  generateEmptyContext,
  GetContextData,
  GetContextHandler,
} from "@/server/requests/get-context";
import { Session } from "next-auth";
import {
  pwaEmptyState,
  PwaReturn,
  useProgressiveWebApp,
} from "@/lib/hooks/use-pwa";
import { Asset, Bid, Favourite, Listing, Loot, Profile } from "@/schema/db";
import { useToast } from "@/lib/hooks/use-toast";
import { useUmami, UmamiTrack } from "@/lib/hooks/use-umami";
import { usePathname } from "next/navigation";

type AppContext = GetContextData & {
  loot: Record<Loot["id"], Loot>;
  listings: Record<Asset["id"], Listing>;
  assets: Record<Asset["id"], Asset>;
  favourites: Record<Asset["id"], Favourite>;
  bids: Record<Bid["id"], Bid>;
  activeProfile: Profile | null;
  pwa: PwaReturn;
  track: UmamiTrack;
  setLive: (data: any) => void;
};

const initialAppContext = () => ({
  ...generateEmptyContext(),
  loot: {},
  listings: {},
  favourites: {},
  assets: {},
  bids: {},
  activeProfile: null,
  pwa: pwaEmptyState,
  track: () => Promise.resolve(),
  setLive: () => undefined,
});

const AppContext = createContext<AppContext>(initialAppContext());

export function useAppContext() {
  return useContext(AppContext);
}

export function AppProvider({
  session,
  context: initialContext = initialAppContext(),
  children,
  getContext,
}: {
  session: Session | null;
  context?: GetContextData;
  children: ReactNode;
  getContext: GetContextHandler;
}) {
  const { toast } = useToast();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const [liveServerTime, setLiveServerTime] = useState<Date | null>(null);
  const pwa = useProgressiveWebApp(session);
  const [liveLoot, setLiveLoot] = useState<AppContext["loot"]>({});
  const [liveBids, setLiveBids] = useState<AppContext["bids"]>({});
  const [liveListings, setLiveListings] = useState<AppContext["listings"]>({});
  const [liveFavourites, setLiveFavourites] = useState<
    AppContext["favourites"]
  >({});
  const [liveAssets, setLiveAssets] = useState<AppContext["assets"]>({});
  const [liveActiveProfile, setLiveActiveProfile] = useState<
    AppContext["activeProfile"]
  >(session?.user.activeProfile || null);
  const track = useUmami(session);

  const [context, getContextAction] = useActionState(async (state) => {
    const response = await getContext();
    if (!("isError" in response)) {
      setLiveServerTime(null);
      // TODO this currently silently errors, do something on error?

      // TODO iterate over notifications returned from getContext
      // toast(
      //     { variant: "info", title: message.title, description: message.body },
      //     message.url ? (
      //       <Button size="xs" variant="outline" asChild>
      //         <Link href={message.url}>view</Link>
      //       </Button>
      //     ) : null
      //   );

      return response;
    }
    return state;
  }, initialContext);

  useEffect(() => {
    startTransition(getContextAction);
  }, [pathname]);

  useEffect(() => {
    // this is a workaround for checking if we're running in a standalone PWA
    // and the user has changed to display-mode fullscreen fullscreen mode
    // see the 'start_url' in manifest.ts
    const url = new URLSearchParams(window.location.search);
    const s = url.get("standalone");
    if (s === "1") {
      sessionStorage.setItem("standalone", s);
    } else if (s === "0") {
      sessionStorage.removeItem("standalone");
    }
  }, []);

  const setLive: AppContext["setLive"] = (data) => {
    startTransition(getContextAction);

    if ("serverTime" in data) {
      setLiveServerTime(data.serverTime);
    }

    if ("activeProfile" in data) {
      setLiveActiveProfile(() => data.activeProfile);
    }

    if ("loot" in data) {
      const loot = data.loot as Loot | Loot[];
      const next = (Array.isArray(loot) ? loot : [loot]).reduce(
        (acc, v) => ({ ...acc, [v.id]: v }),
        {} as Record<Loot["id"], Loot>
      );

      setLiveLoot((prev) => ({ ...prev, ...next }));
    }

    if ("listing" in data) {
      const listing = data.listing as Listing | Listing[];
      const next = (Array.isArray(listing) ? listing : [listing]).reduce(
        (acc, v) => ({ ...acc, [v.assetId]: v }),
        {} as Record<Asset["id"], Listing>
      );

      setLiveListings((prev) => ({ ...prev, ...next }));
    }

    if ("asset" in data) {
      const asset = data.asset as Asset | Asset[];
      const next = (Array.isArray(asset) ? asset : [asset]).reduce(
        (acc, v) => ({ ...acc, [v.id]: v }),
        {} as Record<Asset["id"], Asset>
      );

      setLiveAssets((prev) => ({ ...prev, ...next }));
    }

    if ("favourite" in data) {
      const favourite = data.favourite as Favourite | Favourite[];
      const next = (Array.isArray(favourite) ? favourite : [favourite]).reduce(
        (acc, v) => ({ ...acc, [v.assetId]: v }),
        {} as Record<Asset["id"], Favourite>
      );

      setLiveFavourites((prev) => ({ ...prev, ...next }));
    }

    if ("bid" in data) {
      console.log("BID");
      const bid = data.bid as Bid | Bid[];
      const next = (Array.isArray(bid) ? bid : [bid]).reduce(
        (acc, v) => ({ ...acc, [v.id]: v }),
        {} as Record<Bid["id"], Bid>
      );

      setLiveBids((prev) => ({ ...prev, ...next }));
    }
  };

  const liveContext: AppContext = {
    ...context,
    loot: liveLoot,
    listings: liveListings,
    favourites: liveFavourites,
    assets: liveAssets,
    bids: liveBids,
    activeProfile: liveActiveProfile,
    serverTime: liveServerTime ? liveServerTime : context.serverTime,
    pwa,
    track,
    setLive,
  };
  return (
    <AppContext.Provider value={liveContext}>{children}</AppContext.Provider>
  );
}
